import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import { API_URL, BootstrapTooltip, calculateBalance } from "../../utilities";
import { getRoutes } from "../../utilities/routes";
import { GridRowModesModel, GridToolbarContainer } from "@mui/x-data-grid";
import GridViewMUIX from "../../components/GridViewMUIX";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../context/auth";
import { MouseEvent, useEffect, useState, useCallback } from "react";
import { useNotifications } from "@toolpad/core/useNotifications";

import PopoverComponent from "../../components/PopoverComponent";
import NewUser from "./new-user";
import SkeltonProgress from "../../components/SkeltonProgress";
import { useParams } from "react-router-dom";
import { userColumns } from "./components/userColumns";
import { IAutoCompleteLabelValue } from "../../interface/enum/GeneralType";
import { IBaseDefinition } from "../../interface/IBaseDefinition";
import { BaseDefinitionType } from "../../interface/enum/BaseDefinitionType ";
import { IAppUser } from "../../interface/IUser";
import ImportExcelButton from "./components/import-excel/import-button";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SmsIcon from "@mui/icons-material/Sms";
import LoadingButton from "@mui/lab/LoadingButton";
import moment from "jalali-moment";
import WorkReportDialog from "./components/WorkReport";
import PermissionDialog from "./components/PermissionDialog";
import { usePermissions, PermissionGuard } from "../../utilities/permissions";
import PageTitle from "../../components/PageTitle";
import ExportColumnsDialog from "./components/ExportColumnsDialog";
import BalanceSmsDialog, {
  BalanceSmsType,
} from "./components/BalanceSmsDialog";

const User = () => {
  const Routes = getRoutes();
  const { roleName } = useParams() || "all";

  // State های محلی
  const [getRole, setRole] = useState("all");
  const [rows, setRows] = useState<any>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [openNewUser, setOpenNewUser] = useState<boolean>(false);
  const [getTags, setTags] = useState<IAutoCompleteLabelValue[]>([]);
  const [openWorkReport, setOpenWorkReport] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [copiedUserData, setCopiedUserData] = useState<IAppUser | null>(null);
  const [openPermissionDialog, setOpenPermissionDialog] =
    useState<boolean>(false);
  const [permissionUserId, setPermissionUserId] = useState<string>("");
  const [permissionUserName, setPermissionUserName] = useState<string>("");
  const [openExportDialog, setOpenExportDialog] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >({});
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [openBalanceSmsDialog, setOpenBalanceSmsDialog] =
    useState<boolean>(false);

  // State های صفحه‌بندی
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  // State های فیلتر و سورت
  const [filterValue, setFilterValue] = useState<string>("");
  const [sortModel, setSortModel] = useState<
    Array<{ field: string; sort: "asc" | "desc" }>
  >([{ field: "modifiedOn", sort: "desc" }]);

  // تابع برای تبدیل نام فیلد frontend به فرمت backend
  const mapSortFieldToBackend = (field: string): string => {
    // Mapping خاص برای فیلدهای خاص
    const fieldMapping: Record<string, string> = {
      fullNameAndCompany: "fullnameandcompany",
      nationalCode: "nationalcode",
      codePersonel: "codepersonel",
      phoneNumber: "phonenumber",
      postalCode: "postalcode",
      caseNumber: "casenumber",
      birthDate: "birthdate",
      createdOn: "createdon",
      modifiedOn: "modifiedon",
      fatherName: "fathername",
      telNumber: "telnumber",
      nationalIDBack: "nationalidback",
      coTelNumber: "cotelnumber",
      coTaxFileClass: "cotaxfileclass",
      roleTitle: "roletitle",
      isActive: "isactive",
      userName: "username",
      email: "email",
      province: "province",
      city: "city",
      address: "address",
      fName: "fname",
      lName: "lname",
      coName: "coname",
      economicCode: "economiccode",
      tagRef: "tagref",
      balance: "balance",
      realBalance: "realbalance",
    };

    // اگر mapping خاصی وجود داشت، از آن استفاده کن
    if (fieldMapping[field]) {
      return fieldMapping[field];
    }

    // در غیر این صورت، camelCase را به lowercase تبدیل کن
    return field
      .replace(/([A-Z])/g, (match) => match.toLowerCase())
      .toLowerCase();
  };

  // Hooks
  const notifications = useNotifications();
  const { user } = useAuth();
  const {
    isAdmin,
    isEditor,
    canManageUsers,
    canManageCustomers,
    canCreate,
    canEdit,
    canDelete,
    canView,
  } = usePermissions();

  // تنظیم نقش کاربر بر اساس پارامتر URL
  useEffect(() => {
    if (roleName) {
      setRole(roleName?.charAt(0)?.toUpperCase() + roleName?.slice(1));
    } else {
      setRole("all");
    }
  }, [roleName]);

  // بررسی دسترسی به صفحه
  const canAccessPage = (): boolean => {
    if (isAdmin()) {
      return true;
    }

    if (getRole === "Customer") {
      return canManageCustomers();
    }

    if (getRole === "Editor") {
      return canManageUsers();
    }

    // برای "all" یا سایر موارد، اگر کاربر دسترسی به مدیریت کاربران یا مشتریان دارد
    if (getRole === "all" || getRole === "All") {
      return canManageUsers() || canManageCustomers();
    }

    return false;
  };

  // Query برای دریافت لیست کاربران با صفحه‌بندی، فیلتر و سورت - استفاده از endpoint های بهینه شده
  const { isLoading, error, data, refetch } = useQuery({
    queryKey: [
      "UsersDataPaged",
      getRole,
      paginationModel.page,
      paginationModel.pageSize,
      filterValue,
      sortModel,
    ],
    queryFn: async () => {
      // استفاده از endpoint های با صفحه‌بندی، فیلتر و سورت برای عملکرد بهتر
      const baseUrl =
        getRole === "all" || getRole === "All"
          ? Routes.UsersOptimizedPagedRoutes
          : Routes.UserByRoleOptimizedPagedRoutes + getRole + "/paged";

      // ساخت پارامترهای query
      const params = new URLSearchParams({
        page: paginationModel.page.toString(),
        pageSize: paginationModel.pageSize.toString(),
      });

      // اضافه کردن فیلتر
      if (filterValue && filterValue.trim()) {
        params.append("filter", filterValue.trim());
      }

      // اضافه کردن سورت
      if (sortModel && sortModel.length > 0) {
        const sort = sortModel[0]; // فقط اولین سورت را اعمال می‌کنیم
        // تبدیل نام فیلد frontend به فرمت backend
        const sortField = mapSortFieldToBackend(sort.field);
        params.append("sortField", sortField);
        params.append("sortDir", sort.sort);
      }

      const url = `${baseUrl}?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    },
    enabled: !!user?.token && canAccessPage(), // فقط در صورت وجود توکن و دسترسی اجرا شود
    staleTime: 2 * 60 * 1000, // 2 دقیقه کش
    gcTime: 5 * 60 * 1000, // 5 دقیقه نگهداری در کش
  });

  // Query برای دریافت تگ‌ها (با کش کردن)
  const { data: TagsData } = useQuery({
    queryKey: ["TagsData"],
    queryFn: async () => {
      const response = await fetch(
        Routes.BaseDefinitionWitTypehRoutes + BaseDefinitionType.Tags,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
          },
        }
      );
      return response.json();
    },
    enabled: !!user?.token, // فقط در صورت وجود توکن اجرا شود
    staleTime: 10 * 60 * 1000, // 10 دقیقه کش
    gcTime: 15 * 60 * 1000, // 15 دقیقه نگهداری در کش
  });

  const { mutateAsync: sendBalanceSmsAsync, isPending: isBalanceSmsLoading } =
    useMutation({
      mutationFn: async (data: { text: string; type: BalanceSmsType }) => {
        const response = await fetch(Routes.UsersRoutes + "send-balance-sms", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customerIds: selectedCustomerIds,
            text: data.text,
            type: data.type,
          }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.message ||
              errorData?.title ||
              "خطا در ارسال پیامک مانده واقعی"
          );
        }
        return response.json();
      },
      onSuccess: () => {
        notifications.show("پیامک مانده واقعی ارسال شد", {
          severity: "success",
          autoHideDuration: 3000,
        });
        setOpenBalanceSmsDialog(false);
        setSelectedCustomerIds([]);
        refetch();
      },
      onError: (error: any) => {
        notifications.show(error?.message || "خطا در ارسال پیامک", {
          severity: "error",
          autoHideDuration: 4000,
        });
      },
    });

  // Mutation برای حذف کاربر
  const { mutateAsync: deleteUserAsync, isPending: isDeleteLoading } =
    useMutation({
      mutationFn: async (id: string) => {
        const response = await fetch(Routes.UsersRoutes + id, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
          },
        });
        return response.json();
      },
      onSuccess: () => {
        notifications.show("کاربر با موفقیت حذف شد", {
          severity: "success",
          autoHideDuration: 3000,
        });
        refetch();
      },
      onError: (error) => {
        console.error("خطا در حذف کاربر:", error);
        notifications.show("خطا در حذف کاربر", {
          severity: "error",
          autoHideDuration: 3000,
        });
      },
    });

  // State های مربوط به حذف
  const [anchorDeleteEl, setAnchorDeleteEl] = useState<HTMLElement | null>(
    null
  );
  const [idDeletePopover, setIdDeletePopover] = useState<string>("");
  const [userId, setUserId] = useState<string>("");

  // تنظیم داده‌ها با پشتیبانی از صفحه‌بندی
  useEffect(() => {
    if (error) {
      notifications.show("خطا در دریافت لیست کاربران", {
        severity: "error",
        autoHideDuration: 3000,
      });
    }
    if (data) {
      // بررسی ساختار پاسخ (پشتیبانی از هر دو فرمت: مستقیم و paginated)
      let usersList: any[] = [];
      let totalCount = 0;

      if (data.data) {
        // اگر response ساختار ApiResponse<PagedResult> دارد
        if (data.data.items) {
          // ساختار paginated
          usersList = data.data.items;
          totalCount = data.data.totalCount || 0;
        } else if (Array.isArray(data.data)) {
          // ساختار مستقیم (backward compatibility)
          usersList = data.data;
          totalCount = data.data.length;
        } else {
          usersList = [];
        }
      } else if (Array.isArray(data)) {
        // اگر response مستقیماً آرایه است
        usersList = data;
        totalCount = data.length;
      }

      // ترکیب ستون‌های نام، نام خانوادگی و شرکت
      const processedData = usersList.map((user: any) => ({
        ...user,
        fullNameAndCompany:
          `${user.fName || ""} ${user.lName || ""}`.trim() +
          (user.coName ? `\n${user.coName}` : ""),
      }));

      setRows(processedData);
      setRowCount(totalCount);
    }
  }, [data, error, notifications]);

  // تنظیم تگ‌ها
  useEffect(() => {
    if (TagsData) {
      // تبدیل تگ‌ها به فرمت مورد نیاز برای Autocomplete
      const formattedTags = TagsData.map((tag: any) => ({
        label: tag.caption,
        value: tag.id,
        item: tag,
      }));
      setTags(formattedTags);
    }
  }, [TagsData]);

  // تابع باز کردن دیالوگ کاربر جدید
  const handleOpenNewUser = useCallback(() => {
    setOpenNewUser(true);
    setUserId("");
    setCopiedUserData(null);
  }, []);

  // تابع حذف کاربر
  const handleDelete = useCallback(async () => {
    if (idDeletePopover) {
      await deleteUserAsync(idDeletePopover);
    }
    setAnchorDeleteEl(null);
    setIdDeletePopover("");
  }, [idDeletePopover, deleteUserAsync]);

  // تابع کلیک روی دکمه حذف
  const handleDeleteClick = useCallback(
    (id: string) => (event: MouseEvent<HTMLElement>) => {
      setAnchorDeleteEl(event.currentTarget);
      setIdDeletePopover(id);
    },
    []
  );

  // تابع نمایش جزئیات کاربر
  const handleShowDetailClick = useCallback(
    (id: string) => () => {
      setUserId(id);
      setOpenNewUser(true);
    },
    []
  );

  // تابع باز کردن دیالوگ گزارش کارکرد
  const handleWorkReportClick = useCallback(
    (userId: string, userName: string) => () => {
      setSelectedUserId(userId);
      setSelectedUserName(userName);
      setOpenWorkReport(true);
    },
    []
  );

  // تابع بستن دیالوگ گزارش کارکرد
  const handleCloseWorkReport = useCallback(() => {
    setOpenWorkReport(false);
    setSelectedUserId("");
    setSelectedUserName("");
  }, []);

  // تابع باز کردن دیالوگ سطح دسترسی
  const handlePermissionClick = useCallback(
    (userId: string, userName: string) => () => {
      setPermissionUserId(userId);
      setPermissionUserName(userName);
      setOpenPermissionDialog(true);
    },
    []
  );

  // تابع بستن دیالوگ سطح دسترسی
  const handleClosePermissionDialog = useCallback(() => {
    setOpenPermissionDialog(false);
    setPermissionUserId("");
    setPermissionUserName("");
  }, []);

  const handleOpenBalanceSms = useCallback(() => {
    if (selectedCustomerIds.length === 0) {
      notifications.show("هیچ مشتری انتخاب نشده است", {
        severity: "warning",
        autoHideDuration: 2500,
      });
      return;
    }
    setOpenBalanceSmsDialog(true);
  }, [notifications, selectedCustomerIds.length]);

  const handleSendBalanceSms = useCallback(
    async (data: { text: string; type: BalanceSmsType }) => {
      if (selectedCustomerIds.length === 0) {
        notifications.show("هیچ مشتری انتخاب نشده است", {
          severity: "warning",
          autoHideDuration: 2500,
        });
        return;
      }
      await sendBalanceSmsAsync(data);
    },
    [notifications, selectedCustomerIds, sendBalanceSmsAsync]
  );

  // تابع بستن دیالوگ کاربر جدید
  const handleCloseNewUser = useCallback(() => {
    setOpenNewUser(false);
    setUserId("");
    setCopiedUserData(null);
  }, []);

  // تابع کپی کاربر
  const handleCopyClick = useCallback(
    (userData: IAppUser) => () => {
      setCopiedUserData(userData);
      setUserId(userData.id || ""); // تنظیم userRef برای دریافت اطلاعات کامل
      setOpenNewUser(true);
    },
    []
  );

  // تابع بستن پاپ‌آپ حذف
  const handleDeletePopoverClose = useCallback(() => {
    setAnchorDeleteEl(null);
    setIdDeletePopover("");
  }, []);

  // تابع باز کردن Dialog انتخاب ستون‌ها
  const handleOpenExportDialog = useCallback(() => {
    setOpenExportDialog(true);
  }, []);

  // تابع بستن Dialog انتخاب ستون‌ها
  const handleCloseExportDialog = useCallback(() => {
    setOpenExportDialog(false);
  }, []);

  // تابع export به Excel یا PDF
  const handleExport = useCallback(
    async (selectedFields: string[], exportType: "excel" | "pdf") => {
      setIsExporting(true);
      try {
        // ساخت URL برای export endpoint
        const baseUrl =
          getRole === "all" || getRole === "All"
            ? Routes.UsersRoutes + "export"
            : Routes.UserByRoleRoutes + getRole + "/export";

        const params = new URLSearchParams({
          exportType: exportType,
        });

        // اضافه کردن فیلدهای انتخابی
        selectedFields.forEach((field) => {
          params.append("fields", field);
        });

        const url = `${baseUrl}?${params.toString()}`;

        const response = await fetch(url, {
          method: "GET",
          credentials: "include", // برای CORS با AllowCredentials
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept:
              exportType === "excel"
                ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : "application/pdf",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // دریافت blob
        const blob = await response.blob();

        // ایجاد لینک دانلود
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;

        // نام فایل با تاریخ
        const now = new Date();
        const dateStr = moment(now).format("jYYYY-jMM-jDD_HH-mm");
        const fileName = `کاربران_${getRole}_${dateStr}.${
          exportType === "excel" ? "xlsx" : "pdf"
        }`;

        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        notifications.show(
          `خروجی ${exportType === "excel" ? "Excel" : "PDF"} با موفقیت دریافت شد`,
          {
            severity: "success",
            autoHideDuration: 3000,
          }
        );

        setOpenExportDialog(false);
      } catch (error) {
        console.error("خطا در دریافت خروجی:", error);
        notifications.show("خطا در دریافت خروجی", {
          severity: "error",
          autoHideDuration: 3000,
        });
      } finally {
        setIsExporting(false);
      }
    },
    [getRole, Routes, user?.token, notifications]
  );

  // کامپوننت نوار ابزار
  function EditToolbar() {
    return (
      <GridToolbarContainer>
        <PermissionGuard menuId="customers" permission="create">
          <ImportExcelButton refetch={refetch} notify={notifications} />
        </PermissionGuard>
        <PermissionGuard menuId="customers" permission="view">
          <BootstrapTooltip title="خروجی Excel">
            <IconButton
              size="large"
              color="success"
              onClick={handleOpenExportDialog}
            >
              <FileDownloadIcon />
            </IconButton>
          </BootstrapTooltip>
        </PermissionGuard>
        <PermissionGuard menuId="customers" permission="view">
          <BootstrapTooltip title="خروجی PDF">
            <IconButton
              size="large"
              color="error"
              onClick={handleOpenExportDialog}
            >
              <PictureAsPdfIcon />
            </IconButton>
          </BootstrapTooltip>
        </PermissionGuard>
        <PermissionGuard menuId="customers" permission="view">
          <BootstrapTooltip title="ارسال پیامک">
            <span>
              <IconButton
                size="large"
                color={selectedCustomerIds.length === 0 ? "default" : "primary"}
                disabled={selectedCustomerIds.length === 0}
                onClick={handleOpenBalanceSms}
              >
                <SmsIcon />
              </IconButton>
            </span>
          </BootstrapTooltip>
        </PermissionGuard>
        <PermissionGuard menuId="customers" permission="create">
          <BootstrapTooltip title="کاربر جدید">
            <IconButton
              size="large"
              color="primary"
              component="label"
              onClick={handleOpenNewUser}
            >
              <PersonAddIcon />
            </IconButton>
          </BootstrapTooltip>
        </PermissionGuard>
      </GridToolbarContainer>
    );
  }

  // اگر کاربر دسترسی ندارد، پیام مناسب نمایش دهید
  if (!canAccessPage()) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <h3>شما دسترسی به این صفحه را ندارید</h3>
      </Box>
    );
  }

  return (
    <>
      <PageTitle
        title={
          getRole === "Customer"
            ? "مشتریان"
            : getRole === "Editor"
              ? "کارمندان"
              : "کاربران"
        }
      />
      {/* پاپ‌آپ تایید حذف */}
      <PopoverComponent
        caption="آیا حذف انجام شود؟"
        anchorEl={anchorDeleteEl}
        handlePopoverClose={handleDeletePopoverClose}
        handler={handleDelete}
        id={idDeletePopover}
        isLoading={isDeleteLoading}
        open={Boolean(anchorDeleteEl)}
        key={"delete"}
      />

      {/* دیالوگ کاربر جدید/ویرایش */}
      <NewUser
        usersData={rows || []}
        tags={getTags}
        open={openNewUser}
        setOpenBank={handleCloseNewUser}
        refetch={refetch}
        isEdit={userId !== "" && !copiedUserData}
        userRef={userId}
        setUserId={setUserId}
        userRole={getRole}
        copiedUserData={copiedUserData}
      />

      {/* دیالوگ گزارش کارکرد */}
      <PermissionGuard menuId="tasks" permission="view">
        <WorkReportDialog
          open={openWorkReport}
          onClose={handleCloseWorkReport}
          userId={selectedUserId}
          userName={selectedUserName}
        />
      </PermissionGuard>

      {/* دیالوگ سطح دسترسی */}
      <PermissionGuard menuId="employees" permission="edit">
        <PermissionDialog
          open={openPermissionDialog}
          onClose={handleClosePermissionDialog}
          userId={permissionUserId}
          userName={permissionUserName}
          onSuccess={refetch}
        />
      </PermissionGuard>

      <BalanceSmsDialog
        open={openBalanceSmsDialog}
        onClose={() => setOpenBalanceSmsDialog(false)}
        onSend={handleSendBalanceSms}
        selectedCount={selectedCustomerIds.length}
        isLoading={isBalanceSmsLoading}
      />

      {/* Dialog انتخاب ستون‌ها برای Export */}
      <ExportColumnsDialog
        open={openExportDialog}
        onClose={handleCloseExportDialog}
        columns={userColumns({
          handleDeleteClick,
          handleShowDetailClick,
          handleWorkReportClick:
            isEditor() && canView("tasks") ? handleWorkReportClick : undefined,
          handlePermissionClick:
            isAdmin() || (isEditor() && canEdit("employees"))
              ? handlePermissionClick
              : undefined,
          handleCopyClick,
          getTags,
          getBaseActivities: [],
          getRole,
          canView,
          canEdit,
          canDelete,
          canCreate,
        })}
        onExport={handleExport}
        isExporting={isExporting}
        columnVisibilityModel={columnVisibilityModel}
      />

      {/* جدول کاربران */}
      <Box
        sx={{
          maxWidth: 1024,
          margin: "0 auto", // وسط قرار دادن گرید
          overflow: "hidden",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        {isLoading && <LinearProgress />}

        <GridViewMUIX
          gridUniqueName={"Users" + getRole}
          columns={userColumns({
            handleDeleteClick,
            handleShowDetailClick,
            handleWorkReportClick:
              isEditor() && canView("tasks")
                ? handleWorkReportClick
                : undefined,
            handlePermissionClick:
              isAdmin() || (isEditor() && canEdit("employees"))
                ? handlePermissionClick
                : undefined,
            handleCopyClick,
            getTags,
            getBaseActivities: [], // این فیلد در userColumns استفاده نمی‌شود
            getRole,
            canView,
            canEdit,
            canDelete,
            canCreate,
          })}
          loading={isLoading}
          sortModel={sortModel}
          onSortModelChange={(
            newModel: Array<{ field: string; sort: "asc" | "desc" }>
          ) => {
            setSortModel(newModel);
            // Reset به صفحه اول هنگام تغییر سورت
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          onQuickFilterChange={(value: string) => {
            setFilterValue(value);
            // Reset به صفحه اول هنگام جستجو
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          quickFilterValue={filterValue}
          EditToolbar={EditToolbar}
          rowModesModel={rowModesModel}
          pageSize={paginationModel.pageSize}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          rows={(() => {
            if (!rows || !Array.isArray(rows)) {
              return [];
            }

            const mappedRows = rows
              ?.filter((user: IAppUser) => user.parentRef === null)
              ?.map((user: IAppUser) => {
                try {
                  return {
                    ...user,
                    balance: calculateBalance(
                      user.transactions?.filter(
                        (t) => t.transactionStatus === 1
                      ) ?? []
                    ),
                    realBalance:
                      user.realBalance ??
                      calculateBalance(
                        user.transactions?.filter(
                          (t) => t.transactionStatus === 1
                        ) ?? []
                      ),
                    link: "https://portal.teknohesab.ir/pay/" + user.id,
                  };
                } catch (error) {
                  return user; // Return original user if mapping fails
                }
              });

            return mappedRows || [];
          })()}
          setRowModesModel={setRowModesModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          onSelectionChange={(selectedIds: readonly any[]) =>
            setSelectedCustomerIds(selectedIds as string[])
          }
          rowSelectionModel={selectedCustomerIds}
        />
      </Box>
    </>
  );
};

export default User;
